import React, { useEffect, useRef, useState } from 'react';
import {
  ActionButton,
  ArrowLink,
  ContenidoSurfaceContext,
  Counter,
  IconChoice,
  Picture,
  PlainText,
  Price,
  WordChoice,
} from './editor';
import {
  ContentButton,
  ContentCard,
  ContentCounter,
  ContentDatos,
  ContentDestination,
  ContentLink,
  ContentMember,
  ContentParagraph,
  ContentPrice,
  ContentQuestion,
  ContentRow,
  ContentService,
  ContentTemplateId,
  ContentTestimonial,
  ContentValue,
  emptyDestination,
  emptyTestimonial,
} from './templates';
import './contenido.css';

export type ContenidoMode = 'edit' | 'preview' | 'live';

interface ContenidoBlockProps {
  templateId: ContentTemplateId;
  datos: ContentDatos;
  acento: string;
  animaciones: boolean;
  mode: ContenidoMode;
  replayToken?: number;
  onChange?: (datos: ContentDatos) => void;
  onNotice?: (message: string) => void;
}

function useFonts() {
  useEffect(() => {
    if (document.getElementById('contenido-fonts')) return undefined;
    const link = document.createElement('link');
    link.id = 'contenido-fonts';
    link.rel = 'stylesheet';
    link.href =
      'https://fonts.googleapis.com/css2?family=Lato:wght@400;700&family=Marcellus&family=Montserrat:wght@500;600;700&family=Open+Sans:wght@400;600&display=swap';
    document.head.appendChild(link);
    return undefined;
  }, []);
}

function useReveal(root: React.RefObject<HTMLElement>, replayToken: number, enabled: boolean) {
  useEffect(() => {
    const host = root.current;
    if (!enabled || !host) return undefined;
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          revealed.add(entry.target);
          entry.target.classList.add('in');
          observer.unobserve(entry.target);
        });
      },
      { threshold: 0.18 },
    );
    const frames: number[] = [];
    const revealed = new WeakSet<Element>();
    const reveal = (nodes: Element[]) => {
      if (!nodes.length) return;
      nodes.forEach((node) => {
        revealed.delete(node);
        node.classList.add('anim-reset');
        node.classList.remove('in');
      });
      void host.offsetHeight;
      nodes.forEach((node) => node.classList.remove('anim-reset'));
      frames.push(
        window.requestAnimationFrame(() => {
          nodes.forEach((node) => {
            if (node.isConnected) observer.observe(node);
          });
        }),
      );
    };
    reveal(Array.from(host.querySelectorAll('.anim')));
    const mutations = new MutationObserver((records) => {
      const added: Element[] = [];
      records.forEach((record) => {
        if (record.type === 'attributes') {
          const target = record.target as Element;
          if (revealed.has(target) && !target.classList.contains('in')) {
            target.classList.add('in');
          }
          return;
        }
        record.addedNodes.forEach((node) => {
          if (!(node instanceof Element)) return;
          if (node.classList.contains('anim') && !node.classList.contains('in')) added.push(node);
          added.push(...Array.from(node.querySelectorAll('.anim:not(.in)')));
        });
      });
      reveal(added);
    });
    mutations.observe(host, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ['class'],
    });
    return () => {
      frames.forEach((frame) => window.cancelAnimationFrame(frame));
      observer.disconnect();
      mutations.disconnect();
    };
  }, [enabled, replayToken, root]);
}

export default function ContenidoBlock({
  templateId,
  datos,
  acento,
  animaciones,
  mode,
  replayToken = 0,
  onChange,
  onNotice,
}: ContenidoBlockProps) {
  const editing = mode === 'edit';
  const motion = !editing && animaciones;
  const rootRef = useRef<HTMLElement>(null);
  useFonts();
  useReveal(rootRef, replayToken, motion);
  const patch = (next: ContentDatos) => onChange?.(next);

  const className = [
    'contenido-block mb-8 md:mb-12',
    editing ? 'is-edit' : 'is-preview',
    motion ? 'anim-on' : '',
  ]
    .filter(Boolean)
    .join(' ');

  return (
    <ContenidoSurfaceContext.Provider value={mode}>
      <section className={className} ref={rootRef} style={{ ['--acc' as string]: acento }}>
        {templateId === 'cifras' ? (
          <Cifras
            datos={datos}
            editing={editing}
            motion={motion}
            onChange={patch}
            onNotice={onNotice}
            replayToken={replayToken}
          />
        ) : null}
        {templateId === 'historia' ? (
          <Historia datos={datos} editing={editing} onChange={patch} onNotice={onNotice} />
        ) : null}
        {templateId === 'zigzag' ? (
          <Zigzag datos={datos} editing={editing} onChange={patch} onNotice={onNotice} />
        ) : null}
        {templateId === 'banner' ? (
          <Banner datos={datos} editing={editing} onChange={patch} onNotice={onNotice} />
        ) : null}
        {templateId === 'equipo' ? (
          <Equipo datos={datos} editing={editing} onChange={patch} />
        ) : null}
        {templateId === 'grilla' ? (
          <Grilla datos={datos} editing={editing} onChange={patch} onNotice={onNotice} />
        ) : null}
        {templateId === 'mapa' ? (
          <Mapa datos={datos} editing={editing} onChange={patch} onNotice={onNotice} />
        ) : null}
        {templateId === 'destino' ? (
          <Destino
            datos={datos}
            editing={editing}
            motion={motion}
            onChange={patch}
            onNotice={onNotice}
            replayToken={replayToken}
          />
        ) : null}
        {templateId === 'editorial' ? (
          <Editorial datos={datos} editing={editing} onChange={patch} onNotice={onNotice} />
        ) : null}
        {templateId === 'llamado' ? (
          <Llamado datos={datos} editing={editing} onChange={patch} onNotice={onNotice} />
        ) : null}
        {templateId === 'valores' ? (
          <Valores datos={datos} editing={editing} onChange={patch} />
        ) : null}
        {templateId === 'galeria' ? (
          <Galeria datos={datos} editing={editing} onChange={patch} />
        ) : null}
        {templateId === 'preguntas' ? (
          <Preguntas datos={datos} editing={editing} onChange={patch} />
        ) : null}
        {templateId === 'testimonios' ? (
          <Testimonios
            datos={datos}
            editing={editing}
            motion={motion}
            onChange={patch}
            replayToken={replayToken}
          />
        ) : null}
      </section>
    </ContenidoSurfaceContext.Provider>
  );
}

function Cifras({
  datos,
  editing,
  motion,
  replayToken,
  onChange,
  onNotice,
}: {
  datos: ContentDatos;
  editing: boolean;
  motion: boolean;
  replayToken: number;
  onChange: (datos: ContentDatos) => void;
  onNotice?: (message: string) => void;
}) {
  const counters = datos.contadores as ContentCounter[];
  return (
    <div className='t-cifras'>
      <div className='t-cifras-txt'>
        <PlainText
          anim='fade'
          chip='Título'
          className='t-h2'
          editing={editing}
          onChange={(titulo) => onChange({ ...datos, titulo })}
          path='titulo'
          placeholder='Escribe el título aquí'
          value={String(datos.titulo || '')}
        />
        <PlainText
          anim='fade'
          chip='Descripción'
          className='t-p'
          delay={150}
          editing={editing}
          onChange={(texto) => onChange({ ...datos, texto })}
          path='texto'
          placeholder='Escribe la descripción aquí'
          value={String(datos.texto || '')}
        />
        <div className='anim' data-anim='fade' style={{ ['--d' as string]: '300ms' }}>
          <ActionButton
            editing={editing}
            onChange={(boton) => onChange({ ...datos, boton })}
            onNotice={onNotice}
            path='boton'
            value={datos.boton as ContentButton}
          />
        </div>
      </div>
      <Picture
        background
        className='t-cifras-img'
        editing={editing}
        onChange={(imagen) => onChange({ ...datos, imagen })}
        path='imagen'
        value={String(datos.imagen || '')}
      >
        <div className='t-counters'>
          {counters.map((counter, index) => (
            <Counter
              animate={motion}
              delay={index * 130}
              editing={editing}
              key={index}
              onChange={(next) => {
                const contadores = counters.slice();
                contadores[index] = next;
                onChange({ ...datos, contadores });
              }}
              onNotice={onNotice}
              path={`contadores.${index}`}
              replayToken={replayToken}
              value={counter}
            />
          ))}
        </div>
      </Picture>
    </div>
  );
}

function Historia({
  datos,
  editing,
  onChange,
  onNotice,
}: {
  datos: ContentDatos;
  editing: boolean;
  onChange: (datos: ContentDatos) => void;
  onNotice?: (message: string) => void;
}) {
  const cards = datos.tarjetas as ContentCard[];
  return (
    <div className='t-historia'>
      <div>
        <PlainText
          anim='fade'
          chip='Título'
          className='t-h2'
          editing={editing}
          onChange={(titulo) => onChange({ ...datos, titulo })}
          path='titulo'
          placeholder='Escribe el título aquí'
          value={String(datos.titulo || '')}
        />
        <PlainText
          anim='fade'
          chip='Descripción'
          className='t-p'
          delay={150}
          editing={editing}
          onChange={(texto) => onChange({ ...datos, texto })}
          path='texto'
          placeholder='Escribe la descripción aquí'
          value={String(datos.texto || '')}
        />
        <div className='anim' data-anim='fade' style={{ ['--d' as string]: '300ms' }}>
          <ActionButton
            editing={editing}
            onChange={(boton) => onChange({ ...datos, boton })}
            onNotice={onNotice}
            path='boton'
            value={datos.boton as ContentButton}
          />
        </div>
      </div>
      <div className='t-cards4'>
        {cards.map((card, index) => (
          <article
            className='t-card anim'
            data-anim='flip'
            key={index}
            style={{ ['--d' as string]: `${index * 140}ms` }}
          >
            <IconChoice
              editing={editing}
              onChange={(icono) => {
                const tarjetas = cards.slice();
                tarjetas[index] = { ...card, icono };
                onChange({ ...datos, tarjetas });
              }}
              path={`tarjetas.${index}.icono`}
              value={card.icono}
            />
            <PlainText
              className='t-h4'
              editing={editing}
              onChange={(titulo) => {
                const tarjetas = cards.slice();
                tarjetas[index] = { ...card, titulo };
                onChange({ ...datos, tarjetas });
              }}
              path={`tarjetas.${index}.titulo`}
              placeholder='Título de la tarjeta'
              value={card.titulo}
            />
            <PlainText
              className='t-p-sm'
              editing={editing}
              onChange={(texto) => {
                const tarjetas = cards.slice();
                tarjetas[index] = { ...card, texto };
                onChange({ ...datos, tarjetas });
              }}
              path={`tarjetas.${index}.texto`}
              placeholder='Texto de la tarjeta'
              value={card.texto}
            />
            <ArrowLink
              editing={editing}
              label={card.link.texto || 'Más'}
              onChange={(link) => {
                const tarjetas = cards.slice();
                tarjetas[index] = { ...card, link };
                onChange({ ...datos, tarjetas });
              }}
              onNotice={onNotice}
              path={`tarjetas.${index}.link`}
              value={card.link}
            />
          </article>
        ))}
      </div>
    </div>
  );
}

function Zigzag({
  datos,
  editing,
  onChange,
  onNotice,
}: {
  datos: ContentDatos;
  editing: boolean;
  onChange: (datos: ContentDatos) => void;
  onNotice?: (message: string) => void;
}) {
  const rows = datos.filas as ContentRow[];
  const inverted = Boolean(datos.invertir);
  return (
    <div className='t-zigzag'>
      {editing ? (
        <div className='ed-bar'>
          <button
            className='chip'
            onClick={() => onChange({ ...datos, invertir: !inverted })}
            type='button'
          >
            ⇄ Invertir orden de imágenes
          </button>
        </div>
      ) : null}
      {rows.map((row, index) => {
        const imageFirst = index === 0 ? !inverted : inverted;
        return (
          <div className={imageFirst ? 't-zz-row' : 't-zz-row rev'} key={index}>
            <Picture
              anim={imageFirst ? 'left' : 'right'}
              className='t-zz-img'
              editing={editing}
              onChange={(imagen) => {
                const filas = rows.slice();
                filas[index] = { ...row, imagen };
                onChange({ ...datos, filas });
              }}
              path={`filas.${index}.imagen`}
              value={row.imagen}
            />
            <div
              className={index === 0 ? 't-zz-txt soft anim' : 't-zz-txt anim'}
              data-anim={imageFirst ? 'right' : 'left'}
              style={{ ['--d' as string]: '150ms' }}
            >
              <PlainText
                className='t-ante'
                editing={editing}
                onChange={(ante) => {
                  const filas = rows.slice();
                  filas[index] = { ...row, ante };
                  onChange({ ...datos, filas });
                }}
                path={`filas.${index}.ante`}
                placeholder='Antetítulo'
                value={row.ante}
              />
              <PlainText
                chip={index === 0 ? 'Título' : undefined}
                className='t-h2'
                editing={editing}
                onChange={(titulo) => {
                  const filas = rows.slice();
                  filas[index] = { ...row, titulo };
                  onChange({ ...datos, filas });
                }}
                path={`filas.${index}.titulo`}
                placeholder='Escribe el título aquí'
                value={row.titulo}
              />
              <PlainText
                className='t-p'
                editing={editing}
                onChange={(texto) => {
                  const filas = rows.slice();
                  filas[index] = { ...row, texto };
                  onChange({ ...datos, filas });
                }}
                path={`filas.${index}.texto`}
                placeholder='Escribe la descripción aquí'
                value={row.texto}
              />
              <ActionButton
                editing={editing}
                onChange={(boton) => {
                  const filas = rows.slice();
                  filas[index] = { ...row, boton };
                  onChange({ ...datos, filas });
                }}
                onNotice={onNotice}
                path={`filas.${index}.boton`}
                value={row.boton}
              />
            </div>
          </div>
        );
      })}
    </div>
  );
}

function Banner({
  datos,
  editing,
  onChange,
  onNotice,
}: {
  datos: ContentDatos;
  editing: boolean;
  onChange: (datos: ContentDatos) => void;
  onNotice?: (message: string) => void;
}) {
  const cards = datos.tarjetas as ContentCard[];
  return (
    <div className='t-banner'>
      <Picture
        background
        className='t-banner-bg'
        editing={editing}
        onChange={(imagen) => onChange({ ...datos, imagen })}
        path='imagen'
        value={String(datos.imagen || '')}
      >
        <PlainText
          anim='fade'
          chip='Título'
          className='t-h2'
          editing={editing}
          onChange={(titulo) => onChange({ ...datos, titulo })}
          path='titulo'
          placeholder='Escribe el título aquí'
          value={String(datos.titulo || '')}
        />
      </Picture>
      <div className='t-banner-cards'>
        {cards.map((card, index) => (
          <article
            className='t-bcard anim'
            data-anim='flip'
            key={index}
            style={{ ['--d' as string]: `${index * 140}ms` }}
          >
            <IconChoice
              editing={editing}
              onChange={(icono) => {
                const tarjetas = cards.slice();
                tarjetas[index] = { ...card, icono };
                onChange({ ...datos, tarjetas });
              }}
              path={`tarjetas.${index}.icono`}
              value={card.icono}
            />
            <PlainText
              className='t-h4'
              editing={editing}
              onChange={(titulo) => {
                const tarjetas = cards.slice();
                tarjetas[index] = { ...card, titulo };
                onChange({ ...datos, tarjetas });
              }}
              path={`tarjetas.${index}.titulo`}
              placeholder='Título'
              value={card.titulo}
            />
            <PlainText
              className='t-p-sm'
              editing={editing}
              onChange={(texto) => {
                const tarjetas = cards.slice();
                tarjetas[index] = { ...card, texto };
                onChange({ ...datos, tarjetas });
              }}
              path={`tarjetas.${index}.texto`}
              placeholder='Texto de la tarjeta'
              value={card.texto}
            />
            <ArrowLink
              editing={editing}
              onChange={(link) => {
                const tarjetas = cards.slice();
                tarjetas[index] = { ...card, link };
                onChange({ ...datos, tarjetas });
              }}
              onNotice={onNotice}
              path={`tarjetas.${index}.link`}
              value={card.link}
            />
          </article>
        ))}
      </div>
    </div>
  );
}

function Equipo({
  datos,
  editing,
  onChange,
}: {
  datos: ContentDatos;
  editing: boolean;
  onChange: (datos: ContentDatos) => void;
}) {
  const members = datos.integrantes as ContentMember[];
  function update(index: number, member: ContentMember) {
    const integrantes = members.slice();
    integrantes[index] = member;
    onChange({ ...datos, integrantes });
  }
  return (
    <div className='t-equipo'>
      <div className='t-center'>
        <PlainText
          anim='fade'
          chip='Título'
          className='t-h2'
          editing={editing}
          onChange={(titulo) => onChange({ ...datos, titulo })}
          path='titulo'
          placeholder='Escribe el título aquí'
          value={String(datos.titulo || '')}
        />
      </div>
      <div className='t-team'>
        {members.map((member, index) => (
          <article
            className='t-member anim'
            data-anim='fade'
            key={index}
            style={{ ['--d' as string]: `${index * 120}ms` }}
          >
            {editing && members.length > 2 ? (
              <button
                aria-label='Quitar integrante'
                className='t-remove'
                onClick={() => {
                  if (!window.confirm('¿Querés quitar este integrante?')) return;
                  onChange({ ...datos, integrantes: members.filter((_, item) => item !== index) });
                }}
                type='button'
              >
                ✕
              </button>
            ) : null}
            <Picture
              className='t-avatar'
              editing={editing}
              onChange={(foto) => update(index, { ...member, foto })}
              path={`integrantes.${index}.foto`}
              value={member.foto}
            />
            <div className='t-member-txt'>
              <PlainText
                className='t-cargo'
                editing={editing}
                onChange={(cargo) => update(index, { ...member, cargo })}
                path={`integrantes.${index}.cargo`}
                placeholder='Cargo'
                value={member.cargo}
              />
              <PlainText
                className='t-name'
                editing={editing}
                onChange={(nombre) => update(index, { ...member, nombre })}
                path={`integrantes.${index}.nombre`}
                placeholder='Nombre y apellido'
                value={member.nombre}
              />
              <PlainText
                className='t-p-sm'
                editing={editing}
                onChange={(desc) => update(index, { ...member, desc })}
                path={`integrantes.${index}.desc`}
                placeholder='Breve descripción'
                value={member.desc}
              />
            </div>
          </article>
        ))}
        {editing && members.length < 6 ? (
          <button
            className='t-add'
            onClick={() =>
              onChange({
                ...datos,
                integrantes: [...members, { foto: '', cargo: '', nombre: '', desc: '' }],
              })
            }
            type='button'
          >
            + Agregar integrante
          </button>
        ) : null}
      </div>
    </div>
  );
}

function Testimonios({
  datos,
  editing,
  motion,
  replayToken,
  onChange,
}: {
  datos: ContentDatos;
  editing: boolean;
  motion: boolean;
  replayToken: number;
  onChange: (datos: ContentDatos) => void;
}) {
  const items = datos.items as ContentTestimonial[];
  const [slide, setSlide] = useState(0);
  const current = Math.min(slide, items.length - 1);

  useEffect(() => {
    setSlide(0);
  }, [replayToken]);

  useEffect(() => {
    if (!motion || items.length < 2) return undefined;
    const timer = window.setTimeout(() => setSlide((current + 1) % items.length), 5000);
    return () => window.clearTimeout(timer);
  }, [current, items.length, motion, replayToken]);

  function go(index: number) {
    setSlide((index + items.length) % items.length);
  }

  function update(index: number, item: ContentTestimonial) {
    const next = items.slice();
    next[index] = item;
    onChange({ ...datos, items: next });
  }

  return (
    <Picture
      background
      className='t-testi'
      editing={editing}
      onChange={(imagen) => onChange({ ...datos, imagen })}
      path='imagen'
      value={String(datos.imagen || '')}
    >
      <div className='t-center t-testi-head'>
        <PlainText
          anim='fade'
          chip='Título'
          className='t-h2'
          editing={editing}
          onChange={(titulo) => onChange({ ...datos, titulo })}
          path='titulo'
          placeholder='Escribe el título aquí'
          value={String(datos.titulo || '')}
        />
      </div>
      <div className='t-carousel anim' data-anim='fade' style={{ ['--d' as string]: '200ms' }}>
        <button
          aria-label='Anterior'
          className='t-nav'
          onClick={() => go(current - 1)}
          type='button'
        >
          <svg viewBox='0 0 24 24'>
            <path d='M15 5l-7 7 7 7' />
          </svg>
        </button>
        <div className='t-viewport'>
          <div className='t-track' style={{ transform: `translateX(-${current * 100}%)` }}>
            {items.map((item, index) => (
              <div aria-hidden={index !== current} className='t-slide' key={index}>
                <Picture
                  className='t-avatar'
                  editing={editing}
                  onChange={(foto) => update(index, { ...item, foto })}
                  path={`items.${index}.foto`}
                  value={item.foto}
                />
                <PlainText
                  className='t-quote'
                  editing={editing}
                  onChange={(cita) => update(index, { ...item, cita })}
                  path={`items.${index}.cita`}
                  placeholder='“Escribe el testimonio aquí”'
                  value={item.cita}
                />
                <PlainText
                  className='t-h4'
                  editing={editing}
                  onChange={(nombre) => update(index, { ...item, nombre })}
                  path={`items.${index}.nombre`}
                  placeholder='Nombre'
                  value={item.nombre}
                />
                <PlainText
                  className='t-cargo'
                  editing={editing}
                  onChange={(cargo) => update(index, { ...item, cargo })}
                  path={`items.${index}.cargo`}
                  placeholder='Cargo o referencia'
                  value={item.cargo}
                />
              </div>
            ))}
          </div>
        </div>
        <button
          aria-label='Siguiente'
          className='t-nav'
          onClick={() => go(current + 1)}
          type='button'
        >
          <svg viewBox='0 0 24 24'>
            <path d='M9 5l7 7-7 7' />
          </svg>
        </button>
      </div>
      <div className='t-dots'>
        {items.map((_, index) => (
          <button
            aria-label={`Testimonio ${index + 1}`}
            className={index === current ? 't-dot on' : 't-dot'}
            key={index}
            onClick={() => go(index)}
            type='button'
          />
        ))}
      </div>
      {editing ? (
        <div className='t-testi-ed'>
          {items.length < 5 ? (
            <button
              className='chip'
              onClick={() => {
                onChange({ ...datos, items: [...items, emptyTestimonial()] });
                setSlide(items.length);
              }}
              type='button'
            >
              + Agregar testimonio
            </button>
          ) : null}
          {items.length > 2 ? (
            <button
              className='chip danger'
              onClick={() => {
                if (!window.confirm('¿Querés quitar este testimonio?')) return;
                onChange({ ...datos, items: items.filter((_, index) => index !== current) });
                setSlide(Math.min(current, items.length - 2));
              }}
              type='button'
            >
              ✕ Quitar este testimonio
            </button>
          ) : null}
        </div>
      ) : null}
    </Picture>
  );
}

function Grilla({
  datos,
  editing,
  onChange,
  onNotice,
}: {
  datos: ContentDatos;
  editing: boolean;
  onChange: (datos: ContentDatos) => void;
  onNotice?: (message: string) => void;
}) {
  const services = datos.servicios as ContentService[];
  function update(index: number, service: ContentService) {
    const servicios = services.slice();
    servicios[index] = service;
    onChange({ ...datos, servicios });
  }
  return (
    <div className='t-grilla'>
      <div className='t-center'>
        <PlainText
          anim='fade'
          chip='Título'
          className='t-h2'
          editing={editing}
          onChange={(titulo) => onChange({ ...datos, titulo })}
          path='titulo'
          placeholder='Escribe el título aquí'
          value={String(datos.titulo || '')}
        />
        <PlainText
          anim='fade'
          chip='Descripción'
          className='t-p t-sub'
          delay={120}
          editing={editing}
          onChange={(texto) => onChange({ ...datos, texto })}
          path='texto'
          placeholder='Escribe una bajada aquí'
          value={String(datos.texto || '')}
        />
      </div>
      <div className='t-svcs'>
        {services.map((service, index) => (
          <article
            className='t-svc anim'
            data-anim='fade'
            key={index}
            style={{ ['--d' as string]: `${index * 140}ms` }}
          >
            <div className='t-svc-media'>
              <Picture
                className='t-svc-img'
                editing={editing}
                onChange={(imagen) => update(index, { ...service, imagen })}
                path={`servicios.${index}.imagen`}
                value={service.imagen}
              />
            </div>
            <div className='t-svc-lbl'>
              <PlainText
                className='t-h4'
                editing={editing}
                onChange={(titulo) => update(index, { ...service, titulo })}
                path={`servicios.${index}.titulo`}
                placeholder='Título del servicio'
                value={service.titulo}
              />
              <div className='t-svc-more'>
                <PlainText
                  className='t-p-sm'
                  editing={editing}
                  onChange={(texto) => update(index, { ...service, texto })}
                  path={`servicios.${index}.texto`}
                  placeholder='Texto breve'
                  value={service.texto}
                />
                <ArrowLink
                  editing={editing}
                  onChange={(link) => update(index, { ...service, link })}
                  onNotice={onNotice}
                  path={`servicios.${index}.link`}
                  value={service.link}
                />
              </div>
            </div>
          </article>
        ))}
      </div>
    </div>
  );
}

function Preguntas({
  datos,
  editing,
  onChange,
}: {
  datos: ContentDatos;
  editing: boolean;
  onChange: (datos: ContentDatos) => void;
}) {
  const questions = datos.preguntas as ContentQuestion[];
  const [open, setOpen] = useState(0);
  function toggle(index: number) {
    setOpen(open === index ? -1 : index);
  }
  function update(index: number, question: ContentQuestion) {
    const preguntas = questions.slice();
    preguntas[index] = question;
    onChange({ ...datos, preguntas });
  }
  return (
    <div className='t-faq'>
      <div className='t-center'>
        <PlainText
          anim='fade'
          chip='Título'
          className='t-h2'
          editing={editing}
          onChange={(titulo) => onChange({ ...datos, titulo })}
          path='titulo'
          placeholder='Escribe el título aquí'
          value={String(datos.titulo || '')}
        />
        <PlainText
          anim='fade'
          chip='Descripción'
          className='t-p t-sub'
          delay={120}
          editing={editing}
          onChange={(texto) => onChange({ ...datos, texto })}
          path='texto'
          placeholder='Escribe una bajada aquí'
          value={String(datos.texto || '')}
        />
      </div>
      <div className='t-acc'>
        {questions.map((question, index) => (
          <div
            className={index === open ? 't-acc-item open anim' : 't-acc-item anim'}
            data-anim='fade'
            key={index}
            style={{ ['--d' as string]: `${index * 110}ms` }}
          >
            <div
              className='t-acc-head'
              onClick={() => {
                if (!editing) toggle(index);
              }}
            >
              <PlainText
                className='t-acc-q'
                editing={editing}
                onChange={(pregunta) => update(index, { ...question, pregunta })}
                path={`preguntas.${index}.pregunta`}
                placeholder='Escribe la pregunta'
                value={question.pregunta}
              />
              {editing && questions.length > 2 ? (
                <button
                  aria-label='Quitar pregunta'
                  className='t-remove t-remove-inline'
                  onClick={() => {
                    if (!window.confirm('¿Querés quitar esta pregunta?')) return;
                    onChange({
                      ...datos,
                      preguntas: questions.filter((_, item) => item !== index),
                    });
                    if (open === index) setOpen(-1);
                    if (open > index) setOpen(open - 1);
                  }}
                  type='button'
                >
                  ✕
                </button>
              ) : null}
              <button
                aria-expanded={index === open}
                aria-label='Abrir o cerrar'
                className='t-acc-tog'
                onClick={(event) => {
                  event.stopPropagation();
                  toggle(index);
                }}
                type='button'
              >
                <svg viewBox='0 0 24 24'>
                  <path d='M12 6v12M6 12h12' />
                </svg>
              </button>
            </div>
            <div className='t-acc-body'>
              <div className='t-acc-inner'>
                <PlainText
                  className='t-p-sm t-acc-a'
                  editing={editing}
                  onChange={(respuesta) => update(index, { ...question, respuesta })}
                  path={`preguntas.${index}.respuesta`}
                  placeholder='Escribe la respuesta'
                  value={question.respuesta}
                />
              </div>
            </div>
          </div>
        ))}
        {editing && questions.length < 8 ? (
          <button
            className='t-add t-add-sm'
            onClick={() => {
              onChange({ ...datos, preguntas: [...questions, { pregunta: '', respuesta: '' }] });
              setOpen(questions.length);
            }}
            type='button'
          >
            + Agregar pregunta
          </button>
        ) : null}
      </div>
    </div>
  );
}

function Galeria({
  datos,
  editing,
  onChange,
}: {
  datos: ContentDatos;
  editing: boolean;
  onChange: (datos: ContentDatos) => void;
}) {
  const photos = datos.fotos as string[];
  return (
    <div className='t-galeria'>
      <div className='t-gallery'>
        {photos.map((photo, index) => (
          <div
            className='t-gal-item anim'
            data-anim='flip'
            key={index}
            style={{ ['--d' as string]: `${index * 160}ms` }}
          >
            <Picture
              className='t-gal-img'
              editing={editing}
              onChange={(foto) => {
                const fotos = photos.slice();
                fotos[index] = foto;
                onChange({ ...datos, fotos });
              }}
              path={`fotos.${index}`}
              value={photo}
            />
          </div>
        ))}
      </div>
    </div>
  );
}

function Valores({
  datos,
  editing,
  onChange,
}: {
  datos: ContentDatos;
  editing: boolean;
  onChange: (datos: ContentDatos) => void;
}) {
  const values = datos.valores as ContentValue[];
  function update(index: number, value: ContentValue) {
    const valores = values.slice();
    valores[index] = value;
    onChange({ ...datos, valores });
  }
  return (
    <div className='t-valores'>
      <div className='t-center'>
        <PlainText
          anim='fade'
          chip='Título'
          className='t-h2'
          editing={editing}
          onChange={(titulo) => onChange({ ...datos, titulo })}
          path='titulo'
          placeholder='Escribe el título aquí'
          value={String(datos.titulo || '')}
        />
      </div>
      <div className='t-values'>
        {values.map((value, index) => (
          <article
            className='t-value anim'
            data-anim='flip'
            key={index}
            style={{ ['--d' as string]: `${index * 140}ms` }}
          >
            {editing && values.length > 2 ? (
              <button
                aria-label='Quitar valor'
                className='t-remove'
                onClick={() => {
                  if (!window.confirm('¿Querés quitar este valor?')) return;
                  onChange({ ...datos, valores: values.filter((_, item) => item !== index) });
                }}
                type='button'
              >
                ✕
              </button>
            ) : null}
            <IconChoice
              editing={editing}
              onChange={(icono) => update(index, { ...value, icono })}
              path={`valores.${index}.icono`}
              solid
              value={value.icono}
            />
            <div className='t-value-txt'>
              <PlainText
                className='t-h4'
                editing={editing}
                onChange={(titulo) => update(index, { ...value, titulo })}
                path={`valores.${index}.titulo`}
                placeholder='Nombre del valor'
                value={value.titulo}
              />
              <PlainText
                className='t-p-sm'
                editing={editing}
                onChange={(texto) => update(index, { ...value, texto })}
                path={`valores.${index}.texto`}
                placeholder='Descripción breve'
                value={value.texto}
              />
            </div>
          </article>
        ))}
        {editing && values.length < 6 ? (
          <button
            className='t-add t-add-sm'
            onClick={() =>
              onChange({
                ...datos,
                valores: [...values, { icono: 'check', titulo: '', texto: '' }],
              })
            }
            type='button'
          >
            + Agregar valor
          </button>
        ) : null}
      </div>
    </div>
  );
}

function Llamado({
  datos,
  editing,
  onChange,
  onNotice,
}: {
  datos: ContentDatos;
  editing: boolean;
  onChange: (datos: ContentDatos) => void;
  onNotice?: (message: string) => void;
}) {
  return (
    <div className='t-cta t-center'>
      <PlainText
        anim='fade'
        chip='Título'
        className='t-h2'
        editing={editing}
        onChange={(titulo) => onChange({ ...datos, titulo })}
        path='titulo'
        placeholder='Escribe el título aquí'
        value={String(datos.titulo || '')}
      />
      <Picture
        anim='zoom'
        className='t-cta-img'
        delay={120}
        editing={editing}
        onChange={(imagen) => onChange({ ...datos, imagen })}
        path='imagen'
        value={String(datos.imagen || '')}
      />
      <PlainText
        anim='fade'
        chip='Descripción'
        className='t-p'
        delay={220}
        editing={editing}
        onChange={(texto) => onChange({ ...datos, texto })}
        path='texto'
        placeholder='Escribe la descripción aquí'
        value={String(datos.texto || '')}
      />
      <div className='anim' data-anim='fade' style={{ ['--d' as string]: '320ms' }}>
        <ActionButton
          editing={editing}
          filled
          onChange={(boton) => onChange({ ...datos, boton })}
          onNotice={onNotice}
          path='boton'
          value={datos.boton as ContentButton}
        />
      </div>
    </div>
  );
}

function Editorial({
  datos,
  editing,
  onChange,
  onNotice,
}: {
  datos: ContentDatos;
  editing: boolean;
  onChange: (datos: ContentDatos) => void;
  onNotice?: (message: string) => void;
}) {
  const paragraphs = datos.parrafos as ContentParagraph[];
  const right = datos.lado === 'der';
  const image = String(datos.imagen || '');
  return (
    <div className={right ? 't-edit der' : 't-edit'}>
      <Picture
        className='ed-photo'
        editing={editing}
        onChange={(imagen) => onChange({ ...datos, imagen })}
        path='imagen'
        value={image}
      />
      {editing ? (
        <div className='ed-bar-float'>
          <button
            className='chip'
            onClick={() => onChange({ ...datos, lado: right ? 'izq' : 'der' })}
            type='button'
          >
            {right ? '⇄ Foto a la izquierda' : '⇄ Foto a la derecha'}
          </button>
        </div>
      ) : null}
      <div className='ed-ante-wrap'>
        <PlainText
          className='ed-ante'
          editing={editing}
          onChange={(antetitulo) => onChange({ ...datos, antetitulo })}
          path='antetitulo'
          placeholder='Antetítulo'
          value={String(datos.antetitulo || '')}
        />
      </div>
      <WordChoice
        editing={editing}
        image={image}
        onChange={(palabra) => onChange({ ...datos, palabra })}
        onNotice={onNotice}
        value={String(datos.palabra || '')}
      />
      <div className='ed-content'>
        <PlainText
          anim='fade'
          chip='Subtítulo'
          className='ed-sub'
          delay={500}
          editing={editing}
          onChange={(subtitulo) => onChange({ ...datos, subtitulo })}
          path='subtitulo'
          placeholder='Escribe el subtítulo aquí'
          value={String(datos.subtitulo || '')}
        />
        <div className='ed-body'>
          <span className='ed-line anim' data-anim='line' style={{ ['--d' as string]: '750ms' }} />
          {paragraphs.map((paragraph, index) => (
            <div
              className='ed-par anim'
              data-anim='fade'
              key={index}
              style={{ ['--d' as string]: `${900 + index * 160}ms` }}
            >
              {editing && paragraphs.length > 1 ? (
                <button
                  aria-label='Quitar párrafo'
                  className='t-remove'
                  onClick={() => {
                    if (!window.confirm('¿Querés quitar este párrafo?')) return;
                    onChange({
                      ...datos,
                      parrafos: paragraphs.filter((_, item) => item !== index),
                    });
                  }}
                  type='button'
                >
                  ✕
                </button>
              ) : null}
              <PlainText
                className='ed-p'
                editing={editing}
                onChange={(texto) => {
                  const parrafos = paragraphs.slice();
                  parrafos[index] = { texto };
                  onChange({ ...datos, parrafos });
                }}
                path={`parrafos.${index}.texto`}
                placeholder='Escribe un párrafo aquí'
                value={paragraph.texto}
              />
            </div>
          ))}
          {editing && paragraphs.length < 4 ? (
            <button
              className='t-add t-add-sm'
              onClick={() => onChange({ ...datos, parrafos: [...paragraphs, { texto: '' }] })}
              type='button'
            >
              + Agregar párrafo
            </button>
          ) : null}
          <div
            className='anim'
            data-anim='fade'
            style={{ ['--d' as string]: `${900 + paragraphs.length * 160}ms` }}
          >
            <ActionButton
              editing={editing}
              onChange={(boton) => onChange({ ...datos, boton })}
              onNotice={onNotice}
              path='boton'
              value={datos.boton as ContentButton}
            />
          </div>
        </div>
      </div>
    </div>
  );
}

function Destino({
  datos,
  editing,
  motion,
  replayToken,
  onChange,
  onNotice,
}: {
  datos: ContentDatos;
  editing: boolean;
  motion: boolean;
  replayToken: number;
  onChange: (datos: ContentDatos) => void;
  onNotice?: (message: string) => void;
}) {
  const includes = datos.incluye as string[];
  const duration = String(datos.duracion || '');
  return (
    <Picture
      background
      className='t-dest'
      editing={editing}
      onChange={(imagen) => onChange({ ...datos, imagen })}
      path='imagen'
      value={String(datos.imagen || '')}
    >
      <div className='dest-box'>
        <PlainText
          anim='fade'
          className='dest-ante'
          editing={editing}
          onChange={(antetitulo) => onChange({ ...datos, antetitulo })}
          path='antetitulo'
          placeholder='Antetítulo'
          value={String(datos.antetitulo || '')}
        />
        <PlainText
          anim='fade'
          chip='Destino'
          className='dest-name'
          delay={120}
          editing={editing}
          onChange={(destino) => onChange({ ...datos, destino })}
          path='destino'
          placeholder='Nombre del destino'
          value={String(datos.destino || '')}
        />
        {editing || duration.trim() ? (
          <div className='dest-meta anim' data-anim='fade' style={{ ['--d' as string]: '220ms' }}>
            <svg className='ic-clock' viewBox='0 0 24 24'>
              <circle cx='12' cy='12' r='9' />
              <path d='M12 7v5l3 2' />
            </svg>
            <PlainText
              className='dest-dur'
              editing={editing}
              onChange={(duracion) => onChange({ ...datos, duracion })}
              path='duracion'
              placeholder='Ej: 8 días / 7 noches'
              value={duration}
            />
          </div>
        ) : null}
        <PlainText
          anim='fade'
          chip='Descripción'
          className='dest-txt'
          delay={320}
          editing={editing}
          onChange={(texto) => onChange({ ...datos, texto })}
          path='texto'
          placeholder='Escribe la descripción aquí'
          value={String(datos.texto || '')}
        />
        <div className='dest-inc anim' data-anim='fade' style={{ ['--d' as string]: '420ms' }}>
          {includes.map((item, index) =>
            editing || item.trim() ? (
              <span className='dest-chip' key={index}>
                <svg viewBox='0 0 24 24'>
                  <path d='M5 12.5l4.5 4.5L19 7.5' />
                </svg>
                <PlainText
                  className='dest-inc-t'
                  editing={editing}
                  onChange={(text) => {
                    const incluye = includes.slice();
                    incluye[index] = text;
                    onChange({ ...datos, incluye });
                  }}
                  path={`incluye.${index}`}
                  placeholder='Qué incluye'
                  value={item}
                />
              </span>
            ) : null,
          )}
        </div>
        <div className='dest-row anim' data-anim='fade' style={{ ['--d' as string]: '520ms' }}>
          <Price
            animate={motion}
            editing={editing}
            onChange={(precio) => onChange({ ...datos, precio })}
            onNotice={onNotice}
            path='precio'
            replayToken={replayToken}
            value={datos.precio as ContentPrice}
          />
          <ActionButton
            editing={editing}
            filled
            onChange={(boton) => onChange({ ...datos, boton })}
            onNotice={onNotice}
            path='boton'
            value={datos.boton as ContentButton}
          />
        </div>
      </div>
    </Picture>
  );
}

function pinPosition(destination: ContentDestination) {
  return {
    x: (((Number(destination.lon) || 0) + 180) / 360) * 100,
    y: Math.min(100, Math.max(0, ((80 - (Number(destination.lat) || 0)) / 138) * 100)),
  };
}

function Mapa({
  datos,
  editing,
  onChange,
  onNotice,
}: {
  datos: ContentDatos;
  editing: boolean;
  onChange: (datos: ContentDatos) => void;
  onNotice?: (message: string) => void;
}) {
  const destinations = datos.destinos as ContentDestination[];
  const [selected, setSelected] = useState(editing ? 0 : -1);
  const [drag, setDrag] = useState<{ index: number; x: number; y: number } | null>(null);

  useEffect(() => {
    setSelected(editing ? 0 : -1);
  }, [editing]);

  function update(index: number, destination: ContentDestination) {
    const destinos = destinations.slice();
    destinos[index] = destination;
    onChange({ ...datos, destinos });
  }

  function toggle(index: number) {
    setSelected(selected === index ? -1 : index);
  }

  function startDrag(event: React.PointerEvent<HTMLButtonElement>, index: number) {
    if (!editing) return;
    event.preventDefault();
    const pin = event.currentTarget;
    const area = pin.parentElement?.getBoundingClientRect();
    if (!area) return;
    const startX = event.clientX;
    const startY = event.clientY;
    let moved = false;
    let last = pinPosition(destinations[index]);
    pin.setPointerCapture(event.pointerId);
    const move = (next: PointerEvent) => {
      if (!moved && Math.hypot(next.clientX - startX, next.clientY - startY) < 4) return;
      moved = true;
      last = {
        x: Math.min(100, Math.max(0, ((next.clientX - area.left) / area.width) * 100)),
        y: Math.min(100, Math.max(0, ((next.clientY - area.top) / area.height) * 100)),
      };
      setDrag({ index, ...last });
    };
    const up = () => {
      pin.removeEventListener('pointermove', move);
      pin.removeEventListener('pointerup', up);
      setDrag(null);
      if (!moved) {
        toggle(index);
        return;
      }
      update(index, {
        ...destinations[index],
        lon: Number(((last.x / 100) * 360 - 180).toFixed(2)),
        lat: Number((80 - (last.y / 100) * 138).toFixed(2)),
      });
      setSelected(index);
    };
    pin.addEventListener('pointermove', move);
    pin.addEventListener('pointerup', up);
  }

  return (
    <div className='t-mapa'>
      {editing ? (
        <div className='ed-bar ed-bar-dark'>
          {destinations.length < 8 ? (
            <button
              className='chip'
              onClick={() => {
                onChange({ ...datos, destinos: [...destinations, emptyDestination()] });
                setSelected(destinations.length);
              }}
              type='button'
            >
              + Agregar destino
            </button>
          ) : null}
        </div>
      ) : null}
      <div className='t-center'>
        <PlainText
          anim='fade'
          chip='Título'
          className='t-h2'
          editing={editing}
          onChange={(titulo) => onChange({ ...datos, titulo })}
          path='titulo'
          placeholder='Escribe el título aquí'
          value={String(datos.titulo || '')}
        />
        <PlainText
          anim='fade'
          chip='Descripción'
          className='t-p t-sub'
          delay={120}
          editing={editing}
          onChange={(texto) => onChange({ ...datos, texto })}
          path='texto'
          placeholder='Escribe una bajada aquí'
          value={String(datos.texto || '')}
        />
        <p className='map-hint'>
          {editing
            ? 'Arrastrá los pines para ubicarlos en el mapa · hacé clic en un pin para editar su tarjeta'
            : 'Hacé clic en un destino para ver más'}
        </p>
      </div>
      <div className={selected >= 0 ? 'map-wrap has-card' : 'map-wrap'}>
        <img alt='' aria-hidden='true' className='map-svg' src='/assets/contenido/world-dots.svg' />
        {destinations.map((destination, index) => {
          const position = drag?.index === index ? drag : pinPosition(destination);
          const className = [
            'map-pin',
            'anim',
            index === selected ? 'on' : '',
            drag?.index === index ? 'dragging' : '',
          ]
            .filter(Boolean)
            .join(' ');
          return (
            <button
              aria-label={destination.nombre || 'Destino'}
              className={className}
              data-anim='drop'
              key={index}
              onClick={() => {
                if (!editing) toggle(index);
              }}
              onPointerDown={(event) => startDrag(event, index)}
              style={{
                left: `${position.x}%`,
                top: `${position.y}%`,
                ['--d' as string]: `${300 + index * 130}ms`,
              }}
              type='button'
            >
              <span className='map-pin-lbl'>{destination.nombre || 'Nuevo destino'}</span>
            </button>
          );
        })}
        {destinations.map((destination, index) => {
          const { x, y } = pinPosition(destination);
          const side =
            x > 58 ? { right: `${100 - x}%`, marginRight: 22 } : { left: `${x}%`, marginLeft: 22 };
          const shift = y < 30 ? '-12%' : y > 65 ? '-88%' : '-50%';
          return (
            <div
              className={index === selected ? 'map-card on' : 'map-card'}
              key={index}
              style={{ ...side, top: `${y}%`, ['--ty' as string]: shift }}
            >
              <button
                aria-label='Cerrar'
                className='map-close'
                onClick={() => setSelected(-1)}
                type='button'
              >
                ✕
              </button>
              <Picture
                className='map-card-img'
                editing={editing}
                onChange={(imagen) => update(index, { ...destination, imagen })}
                path={`destinos.${index}.imagen`}
                value={destination.imagen}
              />
              <div className='map-card-body'>
                <PlainText
                  className='t-h4'
                  editing={editing}
                  onChange={(nombre) => update(index, { ...destination, nombre })}
                  path={`destinos.${index}.nombre`}
                  placeholder='Nombre del destino'
                  value={destination.nombre}
                />
                <PlainText
                  className='t-p-sm'
                  editing={editing}
                  onChange={(texto) => update(index, { ...destination, texto })}
                  path={`destinos.${index}.texto`}
                  placeholder='Descripción breve'
                  value={destination.texto}
                />
                <ArrowLink
                  editing={editing}
                  label='Ver más'
                  onChange={(link) => update(index, { ...destination, link })}
                  onNotice={onNotice}
                  path={`destinos.${index}.link`}
                  value={destination.link}
                />
                {editing && destinations.length > 2 ? (
                  <button
                    className='chip danger map-rem'
                    onClick={() => {
                      if (!window.confirm('¿Querés quitar este destino?')) return;
                      onChange({
                        ...datos,
                        destinos: destinations.filter((_, item) => item !== index),
                      });
                      setSelected(-1);
                    }}
                    type='button'
                  >
                    ✕ Quitar destino
                  </button>
                ) : null}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
